<template>
  <v-layout
    align-center
    justify-center
  >
    <v-flex
      xs12
      sm8
      md6
      lg4
    >
      <v-card class="elevation-12 mt-16">
        <v-toolbar
          dark
          color="primary"
        >
          <v-spacer />
          <v-toolbar-title>
            {{ edit ? $t("lbl.updateProfile") : $t("lbl.resigterUser") }}
          </v-toolbar-title>
          <v-spacer />
        </v-toolbar>
        <v-card-text>
          <v-form
            ref="form"
            v-model="valid"
            lazy-validation
          >
            <v-text-field
              v-model="user.email"
              :rules="rules.emailRules"
              :label="$t('lbl.email')"
            />

            <template v-if="!edit">
              <v-text-field
                v-model="user.password"
                :type="show ? 'text' : 'password'"
                :rules="rules.passwordRules"
                :label="$t('lbl.password')"
              >
                <template v-slot:append>
                  <v-icon
                    color="red"
                    @click="show = !show"
                  >{{ show ? "mdi-eye-off" : "mdi-eye" }}</v-icon>
                </template>
              </v-text-field>
              <v-text-field
                v-model="user.confirmPassword"
                :type="show ? 'text' : 'password'"
                :rules="confirmPassword"
                :label="$t('lbl.confirmPassword')"
              />
            </template>
            <template v-else>
              <v-text-field
                v-model="user.password"
                :disabled="locked"
                :type="show ? 'text' : 'password'"
                :rules="!locked ? rules.passwordRules : []"
                :label="$t('lbl.password')"
              >
                <template v-slot:append>
                  <v-icon
                    color="red"
                    @click="show = !show"
                  >{{ show ? "mdi-eye-off" : "mdi-eye" }}</v-icon>
                </template>
                <template v-slot:append-outer>
                  <v-icon
                    color="red"
                    class="lock-button"
                    @click="clear"
                  >mdi-lock</v-icon>
                </template>
              </v-text-field>
              <v-text-field
                v-model="user.confirmPassword"
                :disabled="locked"
                :type="show ? 'text' : 'password'"
                :rules="!locked ? confirmPassword : []"
                :label="$t('lbl.confirmPassword')"
              />
            </template>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="warning"
            class="mr-2"
            @click="reset"
          >{{ $t("lbl.clear") }}</v-btn>

          <v-spacer />

          <v-btn
            :disabled="!valid"
            color="primary"
            @click="save"
          >
            {{ edit ? $t("lbl.update") : $t("lbl.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
      <v-card
        v-if="edit && twofa"
        class="elevation-12 mt-16"
      >
        <v-toolbar
          dark
          color="primary"
        >
          <v-spacer />
          <v-toolbar-title>
            {{ $t('lbl.twofa') }}
          </v-toolbar-title>
          <v-spacer />
        </v-toolbar>
        <v-card-text>
          <div class="d-flex justify-space-between py-2">
            <v-btn
              class="mt-2"
              style="width:16%"
              color="warning"
              @click="renew"
            >
              <v-icon>mdi-autorenew</v-icon>
            </v-btn>
            <v-spacer />
            <v-text-field
              style="width:74%"
              outlined
              disabled
              hide-details
              v-model="secret"
              :label="$t('lbl.otpkey')"
            />
          </div>
        </v-card-text>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapActions } from "vuex";
import {
  required,
  email,
  minLength,
  maxLength,
  nospace,
  password,
  passwordMatch,
} from "../common/validation";

export default {
  data: () => ({
    edit: false,
    valid: true,
    user: {
      email: "",
      password: "",
      confirmPassword: "",
    },
    twofa: false,
    secret: "",
    rules: {
      emailRules: [required, email],
      passwordRules: [required, nospace, minLength(8), maxLength(32), password],
    },
    show: false,
    locked: true,
  }),
  computed: {
    confirmPassword() {
      return [required, passwordMatch(this.user.password)];
    },
  },
  watch: {
    "user.password": {
      handler() {
        this.$nextTick(() => {
          this.$refs.form.validate();
        });
      },
    },
    "user.confirmPassword": {
      handler() {
        this.$nextTick(() => {
          this.$refs.form.validate();
        });
      },
    },
  },
  methods: {
    ...mapActions("user", ["getProfile", "renewTwofa"]),
    ...mapActions("auth", ["register", "profile"]),
    reset() {
      this.$refs.form.reset();
    },
    clear() {
      this.locked = !this.locked;
      this.user.password = "";
      this.user.confirmPassword = "";
    },
    initialize() {
      const sessionUser = this.sessionUser();
      if (sessionUser) {
        this.edit = true;
        this.getProfile()
          .then((user) => {
            Object.assign(this.user, {
              email: user.email,
            });
            this.twofa = user.twofa;
            this.secret = user.secret;
          })
          .catch((error) => {
            this.$toasted.error(this.getMessageText(error.message));
          });
      }
    },
    save() {
      const { confirmPassword, ...user } = { ...this.user };
      if (this.edit && this.locked) {
        delete user.password;
      }
      const request = this.edit ? this.profile(user) : this.register(user);
      request
        .then(() => {
          if (this.edit) {
            this.initialize();
            this.$toasted.success(
              this.$t("msg.update", { item: this.$t("lbl.profile") }),
            );
          } else {
            this.$router.push("/");
            this.$toasted.success(
              this.$t("msg.save", { item: this.$t("lbl.user") }),
            );
          }
        })
        .catch((error) => {
          this.$toasted.error(this.getMessageText(error.message));
        });
    },
    renew() {
      this.$swal({
        html: this.$t("msg.twofaText"),
        icon: "warning",
        showCancelButton: true,
        focusCancel: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("lbl.ok"),
        cancelButtonText: this.$t("lbl.cancel"),
      }).then((result) => {
        if (result.value) {
          this.renewTwofa({ twofa: this.twofa })
            .then(() => {
              this.initialize();
              this.$toasted.success(
                this.$t("msg.update", { item: this.$t("lbl.twofa") }),
              );
            })
            .catch((error) => {
              this.$toasted.error(this.getMessageText(error.message));
            });
        }
      });
    },
  },
  created() {
    this.initialize();
  },
};
</script>

<style scoped>
.lock-button {
  pointer-events: auto;
}

.v-toolbar__title {
  font-size: 1.5rem !important;
}
</style>
